/**
 * @quotech/api - React Query Provider
 * 
 * Pre-configured QueryClientProvider for Quotech modules.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { type ReactNode, useMemo } from 'react'
// Identity-scoped queryKeyHashFn — defence-in-depth against
// cross-tab identity drift (BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT).
// Every `useQuery` cache slot is partitioned by [userId, orgId]
// automatically. Zero call-site changes needed.
import { makeIdentityScopedQueryKeyHashFn } from '@quotech/auth'
// Seam observability (FRAME_TEMPLATE guardrail #6) — one MutationCache +
// one QueryCache tap per client: every mutation fire/settle/error and every
// slow/failed query lands in window.__QT_OBS__ with timing, errors loud on
// the console. Zero per-call-site instrumentation.
import { attachSeamObservability } from './observability'

export interface ApiProviderProps {
  children: ReactNode
  /** Show React Query devtools */
  devtools?: boolean
  /** Custom QueryClient */
  queryClient?: QueryClient
}

/**
 * Default query client configuration. Built lazily so the
 * identity-scoped queryKeyHashFn captures a fresh reference (rather
 * than being baked into a module-scope constant that runs before
 * `pinTabIdentity` has populated sessionStorage at app boot).
 */
function buildDefaultQueryClientOptions() {
  return {
    defaultOptions: {
      queries: {
        // Retry failed requests 3 times
        retry: 3,
        // Refetch on window focus
        refetchOnWindowFocus: false,
        // Stale time: 5 minutes
        staleTime: 5 * 60 * 1000,
        // Cache time: 30 minutes
        gcTime: 30 * 60 * 1000,
        // Identity-scoped cache partitioning — every queryKey hashes
        // to a slot that includes [userId, orgId]. See
        // packages/auth/src/identityScope.ts for the rationale + the
        // BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT defence chain.
        queryKeyHashFn: makeIdentityScopedQueryKeyHashFn(),
      },
      mutations: {
        // Retry failed mutations once
        retry: 1,
      },
    },
  } as const
}

/**
 * Create a configured QueryClient with identity-scoped cache
 * partitioning. Use this everywhere you'd otherwise call
 * `new QueryClient(...)` — that keeps the partitioning consistent
 * across the master SPA and every tenant hub.
 */
export function createQueryClient(): QueryClient {
  const client = new QueryClient(buildDefaultQueryClientOptions())
  attachSeamObservability(client)
  return client
}

/**
 * API Provider component
 * 
 * Wraps your app with React Query provider.
 */
export function ApiProvider({ 
  children, 
  devtools = import.meta.env.MODE === 'development',
  queryClient,
}: ApiProviderProps) {
  const client = useMemo(() => queryClient || createQueryClient(), [queryClient])

  return (
    <QueryClientProvider client={client}>
      {children}
      {devtools && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}

export default ApiProvider

